/* ============================================================
   SHORTCUT "/" — simpanan teks peribadi pelanggan
   ============================================================
   Pelanggan simpan teks sekali di PRO Studio (cth `/promptfashion1`),
   kemudian taip "/" dalam popup Edit Prompts di Workspace untuk
   memasukkannya semula di tempat kursor. (Keputusan user 2026-09-25.)

   Simpanan: `users/{uid}/shortcutSlash/{slot}` — collection BERASINGAN,
   bukan medan dalam doc `users` (had keras 1 MB; data yang boleh membesar
   tidak masuk situ — corak `ctaTemplates`).

   ⭐ ID doc = NOMBOR SLOT (`s001` … `s100`), bukan id rawak. Sengaja:
      Firestore rules tidak boleh MENGIRA bilangan doc, tetapi ia boleh
      menyemak ID — jadi had 100 dikuatkuasakan di SERVER juga.
      ⚠️ KEMBAR: `firestore.rules` → `match /shortcutSlash/{slotId}`.
         Ubah had / corak nama / panjang teks di sini = ubah di sana juga.

   🔒 AKSES (2026-09-26): ciri PRO Studio — WAJIB claim percuma dahulu
      (`utils/ciriPercuma.js`, ciri 'shortcut'), sah sehingga 30 Nov 2026,
      termasuk trial. Tanpa claim / selepas tamat: senarai KEKAL boleh
      dibaca + dipadam, tetapi tidak boleh ditambah, disunting atau dipakai.
      Rules menguatkuasakan perkara yang sama. (Had trial 1 shortcut yang
      diputuskan 25 Sept DIGANTIKAN oleh keputusan claim 26 Sept.)
   ============================================================ */

import { collection, doc, deleteDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';

export const SHORTCUT_COL = 'shortcutSlash';
export const SHORTCUT_MAX = 100;          // ⚠️ KEMBAR firestore.rules (s001–s100)
export const SHORTCUT_TEKS_MAX = 5000;    // ⚠️ KEMBAR firestore.rules

/* Nama: huruf kecil, nombor, garis bawah. 2–40 aksara. ⚠️ KEMBAR rules. */
export const NAMA_CORAK = /^[a-z0-9_]{2,40}$/;

export const slotId = (n) => `s${String(n).padStart(3, '0')}`;

/* "/Prompt Fashion 1" → "prompt_fashion_1". Buang "/" di depan, jarak jadi _,
   aksara lain dibuang. Hasil masih perlu lulus NAMA_CORAK.
   ⚠️ Jangan trim() hujung — fungsi ini dipanggil pada SETIAP ketukan kekunci,
      jadi trim memakan jarak yang baru ditaip sebelum sempat jadi "_". */
export const bersihkanNama = (nama) => String(nama ?? '')
    .replace(/^[\s/]+/, '').toLowerCase()
    .replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
    .slice(0, 40);

/* Teks disimpan seperti ditaip (baris baru kekal) — hanya hujung dipotong. */
export const bersihkanTeks = (teks) => String(teks ?? '').replace(/\s+$/, '').replace(/^\s*\n/, '');

export const bandingShortcut = (a, b) => String(a?.id || '').localeCompare(String(b?.id || ''));

/* Semak sebelum tulis. Pulang kod ralat (string) atau '' bila OK.
   Kod ditukar kepada ayat oleh skrin (i18n). */
export function semakShortcut({ id, nama, teks }, senarai = [], bolehTulis = true) {
    if (!bolehTulis) return 'terkunci';
    const n = bersihkanNama(nama);
    const tk = bersihkanTeks(teks);
    if (!n || !tk.trim()) return 'kosong';
    if (!NAMA_CORAK.test(n)) return 'namaSalah';
    if (tk.length > SHORTCUT_TEKS_MAX) return 'terlaluPanjang';
    if (senarai.some((x) => x.nama === n && x.id !== id)) return 'namaWujud';
    if (!id && senarai.length >= SHORTCUT_MAX) return 'hadPenuh';
    return '';
}

/* Simpan (baru atau sunting).

   Baru → pilih slot kosong paling rendah, dan cipta dalam TRANSAKSI yang
   menyemak slot itu masih kosong. Tanpa transaksi, dua browser yang
   menyimpan serentak boleh memilih slot yang sama dan yang kedua
   menindih yang pertama — senyap. Slot yang ternyata sudah diisi →
   cuba slot seterusnya. */
export async function simpanShortcut({ id, nama, teks }, senarai = [], bolehTulis = true) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('belumLogMasuk');
    const ralat = semakShortcut({ id, nama, teks }, senarai, bolehTulis);
    if (ralat) throw new Error(ralat);

    const muatan = { nama: bersihkanNama(nama), teks: bersihkanTeks(teks), updatedAt: serverTimestamp() };
    const col = collection(db, 'users', uid, SHORTCUT_COL);

    if (id) {
        await runTransaction(db, async (tx) => {
            const rujukan = doc(col, id);
            const snap = await tx.get(rujukan);
            if (!snap.exists()) throw new Error('tiadaLagi');
            tx.update(rujukan, muatan);
        });
        return id;
    }

    const dipakai = new Set(senarai.map((x) => x.id));
    for (let n = 1; n <= SHORTCUT_MAX; n += 1) {
        const sid = slotId(n);
        if (dipakai.has(sid)) continue;
        const rujukan = doc(col, sid);
        const berjaya = await runTransaction(db, async (tx) => {
            const snap = await tx.get(rujukan);
            if (snap.exists()) return false;
            tx.set(rujukan, { ...muatan, createdAt: serverTimestamp() });
            return true;
        });
        if (berjaya) return sid;
        dipakai.add(sid);
    }
    throw new Error('hadPenuh');
}

export async function padamShortcut(id) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('belumLogMasuk');
    if (!id) return;
    await deleteDoc(doc(db, 'users', uid, SHORTCUT_COL, id));
}
